/* eslint-disable react/style-prop-object */
import { HStack, Text, VStack } from 'native-base';
import React from 'react';

import InfoPopover from 'app/components/InfoPopover';
import LinkTicketsPrompt from 'app/components/LinkTicketsPrompt';
import Loading from 'app/components/Loading';
import Notices from 'app/components/Notices';
import ProductLineup from 'app/components/ProductLineup';
import Screen from 'app/components/Screen';
import TicketLogo from 'app/components/TicketLogo';
import { NoticePlacements } from 'app/generated/hygraph';
import useGuest from 'app/hooks/useGuest';
import useGuestDataRefreshControl from 'app/hooks/useGuestDataRefreshControl';
import useMetaData from 'app/hooks/useMetaData';
import useProduct from 'app/hooks/useProduct';
import useRefetchConfigOnFocus from 'app/hooks/useRefetchConfigOnFocus';
import useReservationTextTranslationHelper from 'app/hooks/useReservationTextTranslationHelper';
import { AttractionTicketsStackParamList } from 'app/navigation/AttractionTicketsStackNavigator';
import { getRouteParam } from 'app/navigation/navigationHelper';
import { RootScreenProps } from 'app/navigation/RootNavigator';
import { useI18n } from 'app/providers/I18nProvider';
import Background from 'app/screens/AttractionTicketsScreen/Background';
import ShadowText from 'app/screens/AttractionTicketsScreen/ShadowText';
import Analytics, { Events } from 'app/services/Analytics';
import { getTicketsBackground } from 'app/services/ProductHelper';
import {
  getInstructions,
  getTitle,
  getValidity,
  getValidityLong,
  showValidity,
} from 'app/services/ProductLineupHelper';

export type AttractionTicketsScreenProps = RootScreenProps<
  AttractionTicketsStackParamList,
  'Attraction'
>;

export default function AttractionTicketsScreen({
  navigation,
  route,
}: AttractionTicketsScreenProps) {
  const productContentKey = getRouteParam(route, 'productId') ?? '';
  const { locale } = useI18n();
  const { productContent, defaultProductLineup } = useProduct({ productContentKey });
  const { activeEntitlement } = useGuest();
  const entitlement =
    activeEntitlement?.productContentKey === productContentKey ? activeEntitlement : undefined;
  const rt = useReservationTextTranslationHelper({ product: productContent });
  const productLineup = entitlement?.productLineup ?? defaultProductLineup;
  const { guestDataRefreshControl, guestDataUpdatedAt } = useGuestDataRefreshControl({
    color: 'white',
    _text: {
      shadow: 3,
      bold: true,
      _web: {
        style: {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore: boxShadow is a valid web style prop
          boxShadow: 'none',
          textShadow: '0 1px 5px rgba(0,0,0,.5)',
        },
      },
    },
  });

  useRefetchConfigOnFocus();
  useMetaData('AttractionTickets', { productName: productContent?.name });

  if (!productContentKey) {
    navigation.navigate('Destinations');
    return null;
  }

  if (!productContent || !productLineup) {
    return <Loading testID="AttractionTicketsScreen" />;
  }

  const lineupProps = {
    rt,
    entitlement,
    product: productContent,
  };

  return (
    <Screen
      after={<LinkTicketsPrompt productContentKey={productContentKey} zIndex="2" />}
      before={<Background image={getTicketsBackground(productContent)} />}
      refreshControl={guestDataRefreshControl}
      testID="AttractionTicketsScreen"
      zIndex="2"
    >
      <VStack alignItems="center" justifyContent="center" mb="6" mx="3" pt="6">
        <ShadowText fontSize="xl">{getTitle(productLineup, { ...lineupProps, locale })}</ShadowText>

        {showValidity(productLineup) && (
          <HStack alignItems="center" mt="1" space="1">
            <ShadowText fontSize="lg">{getValidity(productLineup, lineupProps)}</ShadowText>

            <InfoPopover
              _icon={{ color: 'white' }}
              _pressable={{
                shadow: 3,
                style: { shadowOffset: { height: 0, width: 0 } },
                _web: { shadow: '0 !important' },
              }}
              onOpen={() => {
                Analytics.trackEvent(Events.TicketsValidityInfo, { productKey: productContentKey });
              }}
            >
              <Text>{getValidityLong(productLineup, lineupProps)}</Text>
            </InfoPopover>
          </HStack>
        )}

        {!!entitlement && (
          <ShadowText fontSize="md" fontWeight="normal" mt="6">
            {getInstructions(productLineup, lineupProps)}
          </ShadowText>
        )}
      </VStack>

      <Notices
        placement={NoticePlacements.ProductLineupTop}
        productContentKey={productContentKey}
      />

      <VStack mb="12" space="4" width="full">
        <ProductLineup
          entitlement={entitlement}
          productContent={productContent}
          productLineup={productLineup}
        />
      </VStack>

      <TicketLogo logo={productContent.logoTicket} mb="8" mx="auto" w="48" />
      {/* <Dump data={{ productContentKey, entitlement }} /> */}

      {guestDataUpdatedAt}
    </Screen>
  );
}
